import { useEffect, useState, useCallback } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import {
  Building2,
  LayoutDashboard,
  Users,
  DollarSign,
  CreditCard,
  Home,
  Bell,
  Tag,
  LogOut,
  Menu,
  X,
  Shield,
  FileText,
  LifeBuoy
} from 'lucide-react';
import { formatError } from '../utils/errorUtils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Building2, label: 'Agencies', path: '/admin/agencies' },
  { icon: Home, label: 'Properties', path: '/admin/properties' },
  { icon: Users, label: 'Customers', path: '/admin/customers' },
  { icon: FileText, label: 'Reports', path: '/admin/reports' },
  { icon: LifeBuoy, label: 'Tickets', path: '/admin/tickets' },
  { icon: DollarSign, label: 'Revenue', path: '/admin/revenue' },
  { icon: CreditCard, label: 'Subscriptions', path: '/admin/subscriptions' },
  { icon: Tag, label: 'Offers', path: '/admin/offers' },
  { icon: Bell, label: 'Notifications', path: '/admin/notifications' },
];

export default function AdminLayout() {
  const { admin, loading, logout, isAdmin, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/notifications`, {
        headers: getAuthHeader()
      });
      const unread = response.data.notifications.filter(n => !n.read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    // Don't redirect if we're showing the login form
    // The component handles showing login form when not admin
  }, [loading, isAdmin, navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchUnreadCount();
    }
  }, [isAdmin, fetchUnreadCount]);


  if (loading) {
    return (
      <div className="min-h-screen bg-navy-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-12 h-12 text-sky-500 animate-pulse mx-auto mb-4" />
          <p className="text-navy-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (!admin) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center p-8">
        <AdminLoginForm />
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-navy-50" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-navy-900 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-7 h-7 text-sky-500" />
          <span className="font-heading font-bold text-white">Admin Panel</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="text-white"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 bottom-0 w-64 bg-navy-900 z-40 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-navy-800">
            <Link to="/admin" className="flex items-center gap-2">
              <Shield className="w-8 h-8 text-sky-500" />
              <span className="text-xl font-heading font-bold text-white">Admin Panel</span>
            </Link>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${isActive
                    ? 'bg-sky-500 text-white'
                    : 'text-navy-300 hover:bg-navy-800 hover:text-white'
                    }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </div>
                  {item.label === 'Notifications' && unreadCount > 0 && (
                    <Badge className="bg-red-500">{unreadCount}</Badge>
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-navy-800">
            <div className="text-sm text-navy-400 mb-4">
              Logged in as: {admin.email}
            </div>
            <Button
              variant="outline"
              className="w-full justify-start text-navy-300 border-navy-700 hover:bg-navy-800 hover:text-white"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}

function AdminLoginForm() {
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [masterKey, setMasterKey] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginAdmin, registerAdmin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegistering) {
        await registerAdmin(email, password, masterKey);
        setIsRegistering(false);
      } else {
        await loginAdmin(email, password, masterKey);
        navigate('/admin');
      }
    } catch (error) {
      const message = formatError(error.response?.data?.detail) || 'Authentication failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <div className="text-center mb-8">
        <Shield className="w-16 h-16 text-sky-500 mx-auto mb-4" />
        <h1 className="text-2xl font-heading font-bold text-white">
          {isRegistering ? 'Create Admin Account' : 'Admin Login'}
        </h1>
        <p className="text-navy-400 mt-2">Enter your credentials and master key</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 bg-navy-800 p-8 rounded-xl">
        <div className="space-y-2">
          <label className="text-sm text-navy-300">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-12 px-4 bg-navy-700 border border-navy-600 rounded-lg text-white placeholder:text-navy-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
            placeholder="admin@estatecloud.com"
            required
            data-testid="admin-email-input"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm text-navy-300">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full h-12 px-4 bg-navy-700 border border-navy-600 rounded-lg text-white placeholder:text-navy-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
            placeholder="••••••••"
            required
            data-testid="admin-password-input"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm text-navy-300">Master Key</label>
          <input
            type="password"
            value={masterKey}
            onChange={(e) => setMasterKey(e.target.value)}
            className="w-full h-12 px-4 bg-navy-700 border border-navy-600 rounded-lg text-white placeholder:text-navy-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
            placeholder="Enter provided master key"
            required
            data-testid="admin-master-key-input"
          />
        </div>
        <Button
          type="submit"
          className="w-full h-12 bg-sky-500 hover:bg-sky-600"
          disabled={loading}
          data-testid="admin-auth-btn"
        >
          {loading ? 'Processing...' : (isRegistering ? 'Create Account' : 'Sign In')}
        </Button>

        <div className="text-center">
          <button
            type="button"
            onClick={() => setIsRegistering(!isRegistering)}
            className="text-sky-500 hover:text-sky-400 text-sm"
          >
            {isRegistering ? 'Already have an account? Sign In' : 'Need an account? Register'}
          </button>
        </div>
      </form>
    </div>
  );
}
